/*
 * Custom Cart Fees — storefront sync
 *
 * Keeps the optional fee line items (Priority Processing regular/custom and
 * Signature Delivery) in sync with the customer's checkbox selections and the
 * cart contents. The cart itself is the source of truth: a fee line present
 * means that box is checked. The visible checkboxes + totals breakdown are
 * rendered server-side by snippets/cart-fees.liquid, so they survive every
 * section re-render; this script only mutates the fee line items.
 *
 * Fee variants have real prices set on them in the catalog. No Cart Transform
 * function is needed.
 *
 * Requires: assets/pubsub.js (subscribe/publish, PUB_SUB_EVENTS) and
 * window.cartFeesConfig (injected by snippets/cart-fees.liquid).
 */
(function () {
  if (window.__cartFeesInitialized) return;

  var cfg = window.cartFeesConfig;
  if (!cfg || !cfg.enabled || !cfg.priority || !cfg.signature) return;
  if (typeof subscribe !== 'function' || typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
    return;
  }

  window.__cartFeesInitialized = true;

  var PRIORITY_REGULAR = Number(cfg.priority.regular_variant_id);
  var PRIORITY_CUSTOM = Number(cfg.priority.custom_variant_id);
  var SIGNATURE = Number(cfg.signature.variant_id);
  var FEE_VARIANTS = [PRIORITY_REGULAR, PRIORITY_CUSTOM, SIGNATURE].filter(function (v) {
    return v && !isNaN(v);
  });
  // Signature Delivery is mandatory for this customer (tag-based, decided in
  // snippets/cart-fees.liquid; enforced again at checkout by the app).
  var SIGNATURE_REQUIRED = cfg.signature.required === true;
  var CUSTOM_PROPERTY = '_light_bar_config';
  var SOURCE = 'cart-fees';

  var R = (window.routes || {});
  var URL_CART = (R.cart_url || '/cart') ;
  var URL_ADD = R.cart_add_url || '/cart/add.js';
  var URL_CHANGE = R.cart_change_url || '/cart/change.js';

  var busy = false;

  function postConfig(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    };
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function isFee(item) {
    return FEE_VARIANTS.indexOf(item.variant_id) !== -1;
  }

  function realItems(cart) {
    return (cart.items || []).filter(function (it) {
      return !isFee(it);
    });
  }

  function cartIsCustom(cart) {
    return realItems(cart).some(function (it) {
      return it.properties && it.properties[CUSTOM_PROPERTY];
    });
  }

  function qtyOf(cart, variantId) {
    return (cart.items || []).reduce(function (sum, it) {
      return it.variant_id === variantId ? sum + it.quantity : sum;
    }, 0);
  }

  function addFee(variantId) {
    return fetch(URL_ADD, postConfig({ items: [{ id: variantId, quantity: 1 }] }));
  }

  function setFeeQty(variantId, quantity) {
    return fetch(URL_CHANGE, postConfig({ id: String(variantId), quantity: quantity }));
  }

  function removeFee(variantId) {
    return setFeeQty(variantId, 0);
  }

  // Reconcile fee line items against the cart's current state.
  // Returns true if it changed the cart.
  function reconcile() {
    if (busy) return Promise.resolve(false);
    busy = true;

    return getCart()
      .then(function (cart) {
        var chain = Promise.resolve();
        var changed = false;
        var custom = cartIsCustom(cart);
        var hasRealItems = realItems(cart).length > 0;

        var qReg = qtyOf(cart, PRIORITY_REGULAR);
        var qCus = qtyOf(cart, PRIORITY_CUSTOM);
        var qSig = qtyOf(cart, SIGNATURE);
        var prioritySelected = qReg > 0 || qCus > 0;

        // No purchasable items left → strip every fee.
        if (!hasRealItems) {
          if (qReg > 0) { chain = chain.then(function () { return removeFee(PRIORITY_REGULAR); }); changed = true; }
          if (qCus > 0) { chain = chain.then(function () { return removeFee(PRIORITY_CUSTOM); }); changed = true; }
          if (qSig > 0) { chain = chain.then(function () { return removeFee(SIGNATURE); }); changed = true; }
          return chain.then(function () { return changed; });
        }

        // Priority: keep exactly one line, matching the regular/custom mode.
        if (prioritySelected) {
          var wantVar = custom ? PRIORITY_CUSTOM : PRIORITY_REGULAR;
          var otherVar = custom ? PRIORITY_REGULAR : PRIORITY_CUSTOM;
          var wantQty = custom ? qCus : qReg;
          var otherQty = custom ? qReg : qCus;

          if (otherQty > 0) { chain = chain.then(function () { return removeFee(otherVar); }); changed = true; }
          if (wantQty === 0) { chain = chain.then(function () { return addFee(wantVar); }); changed = true; }
          else if (wantQty > 1) { chain = chain.then(function () { return setFeeQty(wantVar, 1); }); changed = true; }
        }

        // Signature: never more than one; always one when mandatory.
        if (qSig > 1) { chain = chain.then(function () { return setFeeQty(SIGNATURE, 1); }); changed = true; }
        else if (qSig === 0 && SIGNATURE_REQUIRED) { chain = chain.then(function () { return addFee(SIGNATURE); }); changed = true; }

        return chain.then(function () { return changed; });
      })
      .then(function (changed) {
        busy = false;
        if (changed) return refresh();
        return false;
      })
      .catch(function (e) {
        busy = false;
        console.error('[cart-fees] reconcile failed', e);
        return false;
      });
  }

  // Clear any leftover loading state on every fee block, regardless of whether
  // a section re-render replaced it. Bulletproofs the spinner on the cart page,
  // where the totals footer lives outside <cart-items> and may not re-render.
  function clearLoading() {
    var blocks = document.querySelectorAll('[data-cart-fees].is-loading');
    for (var i = 0; i < blocks.length; i++) {
      blocks[i].classList.remove('is-loading');
      var sp = blocks[i].querySelector('.cart-fees__spinner');
      if (sp) sp.classList.add('hidden');
    }
    var disabled = document.querySelectorAll('input[data-cart-fee]:disabled');
    for (var j = 0; j < disabled.length; j++) {
      if (!isRequiredInput(disabled[j])) disabled[j].disabled = false;
    }
  }

  // A mandatory fee's checkbox stays checked + disabled at all times.
  function isRequiredInput(input) {
    return input.getAttribute('data-cart-fee-required') === 'true';
  }

  // Re-render the cart sections from the server, then clear loading state.
  function refresh() {
    return getCart().then(function (cart) {
      // Theme handlers update the drawer (items + footer), the cart-items list,
      // and the cart icon.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });

      // On the cart PAGE the totals footer (#main-cart-total — our fee rows)
      // lives OUTSIDE <cart-items>, so the theme's page handler never re-renders
      // it. Refresh it ourselves so the totals update.
      var total = document.getElementById('main-cart-total');
      var mainEl = document.getElementById('main-cart-items');
      if (!total || !mainEl) {
        clearLoading();
        return true;
      }
      // Use the REAL section id (the key in cart.json, e.g. "cart-items"), not
      // the section type "main-cart-items" — otherwise the request returns nothing.
      var sectionId = mainEl.dataset.id || 'cart-items';
      return fetch(URL_CART + '?section_id=' + encodeURIComponent(sectionId))
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var fresh = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#main-cart-total');
          if (fresh) total.innerHTML = fresh.innerHTML;
        })
        .catch(function (e) {
          console.error('[cart-fees] footer refresh failed', e);
        })
        .then(function () {
          clearLoading();
          return true;
        });
    });
  }

  // Show the theme's spinner + disable the checkboxes on the fee block while a
  // toggle syncs. On success the section re-renders (replacing this DOM, so the
  // spinner clears itself); on error we restore it explicitly.
  function setLoading(input, on) {
    var container = input.closest ? input.closest('[data-cart-fees]') : null;
    if (!container) return;
    container.classList.toggle('is-loading', on);
    var spinner = container.querySelector('.cart-fees__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
    var inputs = container.querySelectorAll('input[data-cart-fee]');
    for (var i = 0; i < inputs.length; i++) inputs[i].disabled = on || isRequiredInput(inputs[i]);
  }

  // Handle a checkbox toggle. Delegated so it survives section re-renders.
  function onToggle(input) {
    if (isRequiredInput(input)) {
      // Mandatory fee: never let it be unchecked (e.g. via devtools).
      input.checked = true;
      return;
    }
    if (busy) {
      // A sync is in flight; the imminent re-render will reset the checkbox
      // to the cart's true state, so just ignore this transient click.
      return;
    }
    busy = true;
    setLoading(input, true);
    var fee = input.getAttribute('data-cart-fee');

    getCart()
      .then(function (cart) {
        var custom = cartIsCustom(cart);
        var chain = Promise.resolve();

        if (fee === 'priority') {
          if (input.checked) {
            var wantVar = custom ? PRIORITY_CUSTOM : PRIORITY_REGULAR;
            var otherVar = custom ? PRIORITY_REGULAR : PRIORITY_CUSTOM;
            if (qtyOf(cart, otherVar) > 0) chain = chain.then(function () { return removeFee(otherVar); });
            if (qtyOf(cart, wantVar) === 0) chain = chain.then(function () { return addFee(wantVar); });
          } else {
            if (qtyOf(cart, PRIORITY_REGULAR) > 0) chain = chain.then(function () { return removeFee(PRIORITY_REGULAR); });
            if (qtyOf(cart, PRIORITY_CUSTOM) > 0) chain = chain.then(function () { return removeFee(PRIORITY_CUSTOM); });
          }
        } else if (fee === 'signature') {
          if (input.checked) {
            if (qtyOf(cart, SIGNATURE) === 0) chain = chain.then(function () { return addFee(SIGNATURE); });
          } else {
            if (qtyOf(cart, SIGNATURE) > 0) chain = chain.then(function () { return removeFee(SIGNATURE); });
          }
        }
        return chain;
      })
      .then(function () {
        busy = false;
        return refresh();
      })
      .catch(function (e) {
        busy = false;
        clearLoading();
        console.error('[cart-fees] toggle failed', e);
      });
  }

  document.addEventListener('change', function (event) {
    var input = event.target.closest && event.target.closest('input[data-cart-fee]');
    if (input) onToggle(input);
  });

  // React to any cart change that isn't our own (quantity changes, removals,
  // adding a custom light bar, etc.) and re-sync the fees.
  subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
    if (event && event.source === SOURCE) return;
    reconcile();
  });

  // One pass on load in case the cart changed elsewhere (e.g. returning from
  // checkout, or a custom item added on another tab).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }
})();
